/**
 * Explore collection panel (admin Explore collection tab).
 *
 * - "Run collection" controls for all sources, Google only, or X only. Each
 *   asks the backend to invoke the same collector Lambdas the EventBridge
 *   schedule uses; nothing is collected in the browser.
 * - A confirmation step precedes every trigger (collection spends provider
 *   quota). Buttons are disabled for analysts, where collectors are not
 *   deployed (preview stages), while the trigger request is in flight, and
 *   while a conflicting run is active — the backend lock is the real guard,
 *   this just avoids pointless 409s.
 * - The selected execution's status (polled while active) and a paginated
 *   execution history. Stored trends are available in the sibling Explore tab.
 * - "Stop collection" on a run in progress. It asks the collectors to wind
 *   down at their next checkpoint rather than killing anything — a Lambda
 *   already running cannot be aborted — so the button reports "Stopping…"
 *   until the polled status turns to "Stopped". See the backend's
 *   requestExploreExecutionCancel for what a stop does and does not undo.
 */

import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle,
  Clock,
  Play,
  ArrowsClockwise as RefreshCw,
  Prohibit as StopIcon,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { formatDateTime } from "@/components/explore/format";
import {
  useCancelExploreCollection,
  useExploreExecution,
  useExploreExecutions,
  useSetExploreSourceEnabled,
  useTriggerExploreCollection,
} from "@/hooks/useExploreAdmin";
import { ApiClientError, getErrorMessage } from "@/services/api";
import { EXPLORE_SOURCES, exploreSourceFullLabel } from "@/services/explore";
import type {
  ExploreAdminOverview,
  ExploreExecution,
  ExploreExecutionScope,
  ExploreExecutionStatus,
  ExplorePipelineRun,
  ExplorePipelineState,
  ExplorePipelineStatus,
  ExploreSource,
  ExploreSourceProvider,
} from "@/services/explore";
import { cn } from "@/utils/cn";

type AnyStatus = ExploreExecutionStatus | ExplorePipelineStatus;
type BadgeVariant =
  | "default"
  | "primary"
  | "success"
  | "warning"
  | "destructive"
  | "outline";

const STATUS_VARIANT: Record<AnyStatus, BadgeVariant> = {
  accepted: "primary",
  queued: "primary",
  running: "primary",
  retrying: "warning",
  succeeded: "success",
  partial: "warning",
  failed: "destructive",
  skipped: "outline",
  cancelled: "outline",
};

const STATUS_LABEL: Record<AnyStatus, string> = {
  accepted: "Accepted",
  queued: "Queued",
  running: "Running",
  retrying: "Retrying",
  succeeded: "Completed",
  partial: "Partial",
  failed: "Failed",
  skipped: "Skipped",
  cancelled: "Stopped",
};

const SCOPE_LABEL: Record<ExploreExecutionScope, string> = {
  all: "All enabled sources",
  google: "Google · SerpAPI only",
  serper: "Google · Serper only",
  dataforseo: "Google · DataForSEO only",
  x: "X · X API only",
  twitterapi: "X · TwitterAPI.io only",
};

export function StatusBadge({ status }: { status: AnyStatus }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? "outline"}>
      {STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

function scopeSources(scope: ExploreExecutionScope): ExploreSource[] {
  return scope === "all" ? [...EXPLORE_SOURCES] : [scope];
}

/** True when `scope` would contend for a source lock held by an active run. */
function conflictsWith(
  scope: ExploreExecutionScope,
  active: ExploreExecution[]
): boolean {
  const wanted = scopeSources(scope);
  return active.some((execution) =>
    scopeSources(execution.scope).some((source) => wanted.includes(source))
  );
}

function isStatus(error: unknown, statusCode: number): boolean {
  return error instanceof ApiClientError && error.statusCode === statusCode;
}

function triggerErrorMessage(error: unknown): string {
  if (isStatus(error, 409)) {
    return "A collection run is already in progress for this source. Its status is shown below.";
  }
  if (isStatus(error, 503)) {
    return "Collection is not available in this environment.";
  }
  if (isStatus(error, 403)) {
    return "Only platform admins can run collection.";
  }
  return getErrorMessage(error, "Could not start the collection run.");
}

function executionMessage(execution: ExploreExecution): {
  variant: "info" | "success" | "warning" | "error";
  text: string;
} {
  switch (execution.status) {
    case "accepted":
      return {
        variant: "info",
        text: "Accepted — waiting for the collectors to start.",
      };
    case "running":
      return execution.cancelRequested
        ? {
            variant: "warning",
            text: "Stopping — the collectors finish the provider requests already in flight, then wind down. This usually takes a few seconds.",
          }
        : {
            variant: "info",
            text: "Collection is running. Review stored trends in the other Explore tab.",
          };
    case "cancelled":
      return {
        variant: "warning",
        text: "Stopped. Trends collected before the stop were saved; the remaining topics were not attempted.",
      };
    case "succeeded":
      return { variant: "success", text: "Collection completed." };
    case "partial":
      return {
        variant: "warning",
        text: "Collection finished with errors for part of the run. Check the pipeline details below.",
      };
    case "failed":
      return { variant: "error", text: "Collection failed." };
    case "skipped":
    default:
      return {
        variant: "warning",
        text: "Collection was skipped because another run already held the lock.",
      };
  }
}

function PipelineRow({
  source,
  run,
}: {
  source: ExploreSource;
  run: ExplorePipelineRun;
}) {
  const failures = run.failures ?? [];
  const warnings = run.warnings ?? [];
  return (
    <div
      className="space-y-2 rounded-lg border border-border/60 p-3"
      data-testid={`pipeline-${source}`}
    >
      <div className="flex items-center justify-between gap-2">
        {/* Full name: a pipeline row stands on its own, with no group heading above it. */}
        <span className="text-sm font-medium">
          {exploreSourceFullLabel(source)}
        </span>
        <StatusBadge status={run.status} />
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground sm:grid-cols-3">
        <div>
          <dt className="inline">Trends: </dt>
          <dd className="inline text-foreground">
            {run.trendsCreated ?? 0} new / {run.trendsUpdated ?? 0} updated
          </dd>
        </div>
        <div>
          <dt className="inline">Queries: </dt>
          <dd className="inline text-foreground">{run.queriesWritten ?? 0}</dd>
        </div>
        <div>
          <dt className="inline">Events: </dt>
          <dd className="inline text-foreground">{run.eventsWritten ?? 0}</dd>
        </div>
        <div>
          <dt className="inline">Topics: </dt>
          {/*
            Succeeded over processed, because a run is judged on the RATE,
            not on whether anything at all was written. A bare "30" read as
            healthy on the run where all thirty produced nothing.
          */}
          <dd className="inline text-foreground">
            {run.topicsSucceeded ?? 0}/{run.topicsProcessed ?? 0}
          </dd>
        </div>
        <div>
          <dt className="inline">API requests: </dt>
          <dd className="inline text-foreground">{run.apiRequestsMade ?? 0}</dd>
        </div>
        <div>
          <dt className="inline">Attempts: </dt>
          <dd className="inline text-foreground">{run.attempts ?? 0}</dd>
        </div>
        <div className="col-span-2 sm:col-span-3">
          <dt className="inline">Started: </dt>
          <dd className="inline text-foreground">
            {formatDateTime(run.startedAt)}
          </dd>
          <dt className="ml-3 inline">Finished: </dt>
          <dd className="inline text-foreground">
            {formatDateTime(run.completedAt)}
          </dd>
        </div>
      </dl>
      {failures.length > 0 && (
        <ul className="space-y-1 text-xs text-destructive">
          {failures.map((failure, i) => (
            <li key={i} className="flex gap-1.5">
              <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="break-words">{failure}</span>
            </li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="space-y-1 text-xs text-yellow-700">
          {warnings.map((warning, i) => (
            <li key={i} className="flex gap-1.5">
              <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="break-words">{warning}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ExecutionStatusCard({
  execution,
}: {
  execution: ExploreExecution;
}) {
  const message = executionMessage(execution);
  const sources = EXPLORE_SOURCES.filter(
    (source) => execution.pipelines[source]
  );
  return (
    <div
      className="space-y-3 rounded-xl border border-border bg-card p-4"
      data-testid="execution-status"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Execution
          </p>
          <p className="truncate font-mono text-xs" title={execution.id}>
            {execution.id}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {execution.active && <Spinner size="sm" />}
          <StatusBadge status={execution.status} />
        </div>
      </div>
      <Alert variant={message.variant}>{message.text}</Alert>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
        <div>
          <dt className="inline">Trigger: </dt>
          <dd className="inline capitalize text-foreground">
            {execution.trigger}
            {execution.trigger === "manual" && execution.requestedBy
              ? ` (${execution.requestedBy})`
              : ""}
          </dd>
        </div>
        <div>
          <dt className="inline">Scope: </dt>
          <dd className="inline text-foreground">
            {SCOPE_LABEL[execution.scope]}
          </dd>
        </div>
        <div>
          <dt className="inline">Started: </dt>
          <dd className="inline text-foreground">
            {formatDateTime(execution.startedAt ?? execution.requestedAt)}
          </dd>
        </div>
        <div>
          <dt className="inline">Completed: </dt>
          <dd className="inline text-foreground">
            {execution.active
              ? "In progress"
              : formatDateTime(execution.completedAt)}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="inline">Totals: </dt>
          <dd className="inline text-foreground">
            {execution.totals.trendsCreated} trends created,{" "}
            {execution.totals.trendsUpdated} updated,{" "}
            {execution.totals.queriesWritten} queries,{" "}
            {execution.totals.eventsWritten} events
          </dd>
        </div>
      </dl>
      <div className="grid gap-2 md:grid-cols-2">
        {sources.map((source) => {
          const run = execution.pipelines[source];
          return run ? (
            <PipelineRow key={source} source={source} run={run} />
          ) : null;
        })}
      </div>
    </div>
  );
}

function stopErrorMessage(error: unknown): string {
  if (isStatus(error, 409)) {
    return "This run had already finished, so there was nothing to stop.";
  }
  if (isStatus(error, 403)) {
    return "Only platform admins can stop collection.";
  }
  return getErrorMessage(error, "Could not stop the collection run.");
}

function SelectedExecution({ id, canStop }: { id: string; canStop: boolean }) {
  const { data, isLoading, error, refetch, isFetching } =
    useExploreExecution(id);
  const cancel = useCancelExploreCollection();
  const [confirmStop, setConfirmStop] = useState(false);
  if (isLoading) {
    return (
      <div className="flex h-24 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
        {getErrorMessage(error, "Failed to load execution status.")}{" "}
        <button className="underline" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }
  // Only an in-flight run can be stopped, and only once: after the request
  // lands the run is already winding down and pressing again would do
  // nothing but send the same flag a second time.
  const stopReason = !canStop
    ? "Only platform admins can stop collection."
    : !data.active
      ? "This run has already finished."
      : data.cancelRequested
        ? "Already stopping."
        : cancel.isPending
          ? "Stopping…"
          : null;

  return (
    <div className="space-y-2">
      <ExecutionStatusCard execution={data} />
      {cancel.isError && (
        <Alert variant={isStatus(cancel.error, 409) ? "warning" : "error"}>
          {stopErrorMessage(cancel.error)}
        </Alert>
      )}
      <div className="flex justify-end gap-2">
        {(data.active || data.cancelRequested) && (
          <Button
            variant="outline"
            size="sm"
            data-testid="stop-collection"
            disabled={stopReason !== null}
            title={stopReason ?? undefined}
            onClick={() => setConfirmStop(true)}
          >
            <StopIcon className="mr-1.5 h-3.5 w-3.5" />
            {data.cancelRequested ? "Stopping…" : "Stop collection"}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn("mr-1.5 h-3.5 w-3.5", isFetching && "animate-spin")}
          />
          Refresh status
        </Button>
      </div>

      <Dialog
        open={confirmStop}
        onOpenChange={(open) => {
          if (!open && !cancel.isPending) setConfirmStop(false);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Stop this collection run?</DialogTitle>
            <DialogDescription>
              The collectors stop at their next checkpoint, within a few
              seconds. Trends already collected stay saved; the topics not yet
              reached are abandoned and the run is recorded as stopped. Provider
              requests already made are still billed — stopping bounds what the
              run will spend from here, it does not refund what it has spent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmStop(false)}
              disabled={cancel.isPending}
            >
              Keep running
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                cancel.mutate(id, { onSettled: () => setConfirmStop(false) })
              }
              disabled={cancel.isPending}
            >
              {cancel.isPending ? "Stopping…" : "Stop collection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExecutionHistory({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  const [cursor, setCursor] = useState<string | undefined>();
  const [previous, setPrevious] = useState<ExploreExecution[]>([]);
  const { data, isLoading, error, refetch } = useExploreExecutions(cursor);
  const items = [...previous, ...(data?.items ?? [])];

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Execution history</h3>
      {isLoading && items.length === 0 ? (
        <div className="flex h-16 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {getErrorMessage(error, "Failed to load execution history.")}{" "}
          <button className="underline" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No collection runs recorded yet.
        </p>
      ) : (
        <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
          {items.map((execution) => (
            <li key={execution.id}>
              <button
                onClick={() => onSelect(execution.id)}
                className={cn(
                  "flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-muted/50",
                  execution.id === selectedId && "bg-muted/60"
                )}
              >
                <span className="flex min-w-0 flex-wrap items-center gap-x-2">
                  <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span>{formatDateTime(execution.requestedAt)}</span>
                  <span className="capitalize text-muted-foreground">
                    {execution.trigger} · {SCOPE_LABEL[execution.scope]}
                  </span>
                </span>
                <StatusBadge status={execution.status} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {data?.hasMore && data.cursor && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setPrevious((prev) => [...prev, ...data.items]);
            setCursor(data.cursor);
          }}
        >
          Load older runs
        </Button>
      )}
    </div>
  );
}

/**
 * One provider: its last-run health, the switch that governs its SCHEDULED
 * collection, and a button to run it by hand.
 *
 * The switch and the button are deliberately independent. Switching a
 * provider off stops the schedule spending on it; running it from here is an
 * explicit, audited decision, so it stays available either way — that is how
 * a provider gets tested before it is put on the schedule.
 */
function formatUsd(amount: number): string {
  return amount >= 0.01
    ? `$${amount.toFixed(2)}`
    : `$${amount.toFixed(4).replace(/0+$/, "").replace(/\.$/, ".0")}`;
}

/**
 * The last run's efficiency, as the numbers a provider is actually chosen
 * on.
 *
 * Request counts alone say nothing useful across vendors — SerpAPI, Serper
 * and DataForSEO each price a request differently — so the figure that
 * matters is what a stored trend cost. Coverage sits beside it because a
 * provider can look cheap by collecting almost nothing. Both are list-price
 * estimates, not billed amounts, and are labelled as such.
 */
function ProviderStats({ state }: { state: ExplorePipelineState }) {
  const parts: string[] = [];
  if (state.topicsSucceeded !== undefined && state.topicsProcessed) {
    parts.push(`${state.topicsSucceeded}/${state.topicsProcessed} topics`);
  }
  if (state.opportunitiesSaved !== undefined) {
    parts.push(`${state.opportunitiesSaved} trends`);
  }
  if (state.apiRequestsMade !== undefined) {
    parts.push(`${state.apiRequestsMade} requests`);
  }
  if (state.costPerTrendUsd !== undefined) {
    parts.push(`~${formatUsd(state.costPerTrendUsd)}/trend`);
  } else if (state.estimatedCostUsd !== undefined) {
    parts.push(`~${formatUsd(state.estimatedCostUsd)}`);
  }
  if (!parts.length) return null;

  return (
    <p className="text-xs text-muted-foreground" data-testid="provider-stats">
      {parts.join(" · ")}
    </p>
  );
}

function ProviderRow({
  provider,
  canTrigger,
  runDisabledReason,
  onRun,
}: {
  provider: ExploreSourceProvider;
  canTrigger: boolean;
  runDisabledReason: string | null;
  onRun: () => void;
}) {
  const setEnabled = useSetExploreSourceEnabled();
  const state = provider.state;
  const toggleDisabled =
    !canTrigger || !provider.available || setEnabled.isPending;
  const toggleReason = !canTrigger
    ? "Only platform admins can change collection sources."
    : !provider.available
      ? "This collector is not deployed in this environment."
      : null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border/60 px-3 py-2.5"
      data-testid={`source-${provider.source}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{provider.label}</span>
          {!provider.enabled && (
            <Badge variant="outline">Off the schedule</Badge>
          )}
          {state?.status === "failed" ? (
            <XCircle
              className="h-4 w-4 shrink-0 text-destructive"
              aria-label="Last run failed"
            />
          ) : state?.status ? (
            <CheckCircle
              className="h-4 w-4 shrink-0 text-green-600"
              aria-label={`Last run ${state.status}`}
            />
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          {state?.lastSuccessAt
            ? `Last success ${formatDateTime(state.lastSuccessAt)}`
            : "No successful run yet"}
        </p>
        {state && <ProviderStats state={state} />}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={runDisabledReason !== null}
          title={runDisabledReason ?? undefined}
          onClick={onRun}
        >
          <Play className="mr-1.5 h-3.5 w-3.5" />
          Run
        </Button>
        <button
          type="button"
          role="switch"
          aria-checked={provider.enabled}
          aria-label={`Scheduled collection for ${provider.label}`}
          disabled={toggleDisabled}
          title={toggleReason ?? undefined}
          onClick={() =>
            setEnabled.mutate({
              source: provider.source,
              enabled: !provider.enabled,
            })
          }
          className={cn(
            "relative h-5 w-9 shrink-0 rounded-full transition-colors",
            provider.enabled ? "bg-primary" : "bg-muted-foreground/40",
            toggleDisabled && "cursor-not-allowed opacity-50"
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform",
              provider.enabled ? "translate-x-4" : "translate-x-0.5"
            )}
          />
        </button>
      </div>
      {setEnabled.isError && (
        <p className="w-full text-xs text-destructive">
          {getErrorMessage(setEnabled.error, "Could not change this source.")}
        </p>
      )}
    </div>
  );
}

export function ExploreCollectionPanel({
  overview,
}: {
  overview: ExploreAdminOverview;
}) {
  const trigger = useTriggerExploreCollection();
  const [confirmScope, setConfirmScope] =
    useState<ExploreExecutionScope | null>(null);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const { data: history } = useExploreExecutions();

  const active = overview.activeExecutions;
  const firstActiveId = active[0]?.id;
  const latestId = history?.items[0]?.id;

  // Follow a newly active run (manual, scheduled, or surfaced after a 409);
  // otherwise default to the most recent run.
  useEffect(() => {
    if (firstActiveId) setSelectedId(firstActiveId);
  }, [firstActiveId]);
  useEffect(() => {
    setSelectedId((current) => current ?? latestId);
  }, [latestId]);

  const groups = overview.sourceGroups ?? [];
  const providers = useMemo(
    () => groups.flatMap((group) => group.providers),
    [groups]
  );
  const unavailable = useMemo(
    () =>
      providers
        .filter((provider) => !provider.available)
        .map((provider) => provider.source),
    [providers]
  );
  const enabledCount = providers.filter(
    (provider) => provider.enabled && provider.available
  ).length;

  function disabledReason(scope: ExploreExecutionScope): string | null {
    if (!overview.canTrigger) return "Only platform admins can run collection.";
    if (scope === "all" && enabledCount === 0) {
      return "Every source is switched off. Run one directly instead.";
    }
    if (scope !== "all" && unavailable.includes(scope)) {
      return "Not available in this environment.";
    }
    if (trigger.isPending) return "Starting…";
    if (conflictsWith(scope, active)) return "A run is already in progress.";
    return null;
  }

  function handleConfirm() {
    if (!confirmScope) return;
    trigger.mutate(confirmScope, {
      onSuccess: (execution) => setSelectedId(execution.id),
      onSettled: () => setConfirmScope(null),
    });
  }

  return (
    <section className="space-y-4 rounded-xl border border-border bg-card/50 p-4 sm:p-5">
      <div className="space-y-1">
        <h2 className="text-base font-semibold">Collection</h2>
        <p className="text-sm text-muted-foreground">
          Google is collected by three independent providers — SerpAPI, Serper
          and DataForSEO — and X by two, the official X API and TwitterAPI.io.
          Each has its own credentials, schedule and run lock, so one provider
          failing never stops another. Users see only Google and X.
        </p>
      </div>

      <div className="space-y-3">
        {groups.map((group) => (
          <div key={group.source} className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </h3>
            <div className="space-y-2">
              {group.providers.map((provider) => (
                <ProviderRow
                  key={provider.source}
                  provider={provider}
                  canTrigger={overview.canTrigger}
                  runDisabledReason={disabledReason(provider.source)}
                  onRun={() => setConfirmScope(provider.source)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        A source switched off is skipped by its schedule and spends no provider
        quota. Running it from here still works, so a provider can be tested
        before it goes on the schedule.
      </p>

      {unavailable.length > 0 && (
        <Alert variant="info">
          Collectors for{" "}
          {unavailable.map((s) => exploreSourceFullLabel(s)).join(", ")} are not
          deployed in this environment (preview stages have no scheduled
          collection), so manual runs are disabled. Stored trends can still be
          browsed.
        </Alert>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {(() => {
          const reason = disabledReason("all");
          return (
            <Button
              variant="default"
              size="sm"
              disabled={reason !== null}
              title={reason ?? undefined}
              onClick={() => setConfirmScope("all")}
              className="w-full sm:w-auto"
            >
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Run every enabled source
            </Button>
          );
        })()}
      </div>
      {!overview.canTrigger && (
        <p className="text-xs text-muted-foreground">
          You have read-only access. Only platform admins can run collection.
        </p>
      )}

      {trigger.isError && (
        <Alert variant={isStatus(trigger.error, 409) ? "warning" : "error"}>
          {triggerErrorMessage(trigger.error)}
        </Alert>
      )}

      {selectedId ? (
        <SelectedExecution id={selectedId} canStop={overview.canTrigger} />
      ) : (
        <p className="text-sm text-muted-foreground">
          No collection run selected.
        </p>
      )}

      <ExecutionHistory selectedId={selectedId} onSelect={setSelectedId} />

      <Dialog
        open={confirmScope !== null}
        onOpenChange={(open) => {
          if (!open && !trigger.isPending) setConfirmScope(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Run {confirmScope ? SCOPE_LABEL[confirmScope] : ""}?
            </DialogTitle>
            <DialogDescription>
              This calls the external trend providers now and uses API quota. It
              can take several minutes; you can keep using the dashboard while
              it runs.
              {confirmScope &&
                confirmScope !== "all" &&
                !providers.find((p) => p.source === confirmScope)?.enabled &&
                " This source is switched off for scheduled collection — running it here is a one-off."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmScope(null)}
              disabled={trigger.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={trigger.isPending}>
              {trigger.isPending ? "Starting…" : "Start collection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
